import React, { useEffect, useState, useCallback } from 'react';
import AxiosInstance from 'axiosConfig';
import { getItem } from 'utils/localStorage';

const DEFAULT_PROPS = {
  name: '',
  data_type: '0',
  que_topic: '0',
  bot_act_as: '0',
  que_category: '0',
  is_action_trigger: false,
  expects_yes_no: false,
  ans_desc: '',
  node_key_words: '',
  is_ussd_prompt: false,
  stems_logic_in_num: false,
  function_details: {}
};

const INITIAL_DROPDOWN_OPTIONS = {
  data_types: {},
  topics: {},
  bot_acts_as: {},
  node_category: {}
};

const PropertiesPanel = ({ element, modeler }) => {
  const [state, setState] = useState({
    properties: DEFAULT_PROPS,
    dropdownOptions: INITIAL_DROPDOWN_OPTIONS,
    loading: true,
    error: null,
    nameError: '',
    availableFunctions: [],
    selectedFunction: '',
    inputJsonFields: {},
    taskOptions: [],
    useCustomType: false,
    customTypeFields: { regex: '', errorMessage: '' }
  });

  const {
    properties,
    dropdownOptions,
    loading,
    error,
    nameError,
    availableFunctions,
    selectedFunction,
    inputJsonFields,
    taskOptions,
    useCustomType,
    customTypeFields
  } = state;

  // Validation functions
  const validateName = useCallback((name) => {
    let error = '';
    if (!name.trim()) {
      error = 'Task name is required and cannot be empty';
    } else if (!/^[a-zA-Z0-9]+(_[a-zA-Z0-9]+)*$/.test(name)) {
      error = 'Task name must be a single word or words joined with underscores (e.g., Task_Name)';
    } else if (modeler) {
      const elementRegistry = modeler.get('elementRegistry');
      const elements = elementRegistry.getAll();
      const isDuplicate = elements.some(
        el => el.type === 'bpmn:Task' && 
              el.id !== element?.id && 
              el.businessObject.name?.toLowerCase() === name.toLowerCase()
      );
      if (isDuplicate) error = 'Task name must be unique (case-insensitive)';
    }

    setState(prev => ({ ...prev, nameError: error }));
    return !error;
  }, [modeler, element]);

  // Data fetching effects
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const cachedData = getItem('node-extra-properties');
        if (cachedData) {
          const parsedData = typeof cachedData === 'string' ? JSON.parse(cachedData) : cachedData;
          if (parsedData?.data_types && parsedData?.topics && parsedData?.['bot_acts-as'] && parsedData?.node_category) {
            setState(prev => ({
              ...prev,
              dropdownOptions: {
                data_types: parsedData.data_types,
                topics: parsedData.topics,
                bot_acts_as: parsedData['bot_acts-as'],
                node_category: parsedData.node_category
              },
              loading: false
            }));
            return;
          }
        }

        const response = await AxiosInstance.get('/node-extra-properties');
        const responseData = response.data || {};
        localStorage.setItem('node-extra-properties', JSON.stringify(responseData));
        
        setState(prev => ({
          ...prev,
          dropdownOptions: {
            data_types: responseData.data_types || {},
            topics: responseData.topics || {},
            bot_acts_as: responseData['bot_acts-as'] || {},
            node_category: responseData.node_category || {}
          },
          loading: false
        }));
      } catch (err) {
        setState(prev => ({
          ...prev,
          error: 'Failed to load dropdown options from API',
          dropdownOptions: INITIAL_DROPDOWN_OPTIONS,
          loading: false
        }));
      }
    };

    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    if (properties.is_action_trigger) {
      const fetchFunctions = async () => {
        try {
          const response = await AxiosInstance.get('/bpmns-custom-functions');
          const functions = response.data.message || [];
          
          setState(prev => ({
            ...prev,
            availableFunctions: functions,
            ...(selectedFunction && !functions.find(f => f.id.toString() === selectedFunction) && {
              selectedFunction: '',
              inputJsonFields: {}
            })
          }));
        } catch (err) {
          setState(prev => ({ ...prev, error: err.message }));
        }
      };
      fetchFunctions();
    } else {
      setState(prev => ({
        ...prev,
        availableFunctions: [],
        selectedFunction: '',
        inputJsonFields: {},
        error: null
      }));
    }
  }, [properties.is_action_trigger, selectedFunction]);

  useEffect(() => {
    if (modeler) {
      const elementRegistry = modeler.get('elementRegistry');
      const elements = elementRegistry.getAll();
      const tasks = elements
        .filter(el => el.type === 'bpmn:Task' && el.businessObject.name)
        .map(el => ({ id: el.id, name: el.businessObject.name }));
      
      setState(prev => ({ ...prev, taskOptions: tasks }));
    }
  }, [modeler, element]);

  // Load element properties
  useEffect(() => {
    if (!element?.businessObject) return;

    const bo = element.businessObject;
    const loadedProps = { ...DEFAULT_PROPS, name: bo.get('name') || '' };

    Object.keys(DEFAULT_PROPS).forEach(key => {
      if (key !== 'name') {
        const value = bo.get(`node:${key}`);
        if (value !== undefined) {
          try {
            if (key === 'data_type') {
              // Check if data_type is a custom type (JSON object)
              try {
                const parsed = JSON.parse(value);
                if (parsed && typeof parsed === 'object' && 'regex' in parsed && 'error_message' in parsed) {
                  loadedProps[key] = 'custom';
                  setState(prev => ({
                    ...prev,
                    useCustomType: true,
                    customTypeFields: { regex: parsed.regex, errorMessage: parsed.error_message }
                  }));
                } else {
                  // Handle legacy list format or invalid JSON
                  if (Array.isArray(parsed) && parsed.length === 2) {
                    loadedProps[key] = 'custom';
                    setState(prev => ({
                      ...prev,
                      useCustomType: true,
                      customTypeFields: { regex: parsed[0], errorMessage: parsed[1] }
                    }));
                  } else {
                    loadedProps[key] = value;
                    setState(prev => ({
                      ...prev,
                      useCustomType: false,
                      customTypeFields: { regex: '', errorMessage: '' }
                    }));
                  }
                }
              } catch {
                loadedProps[key] = value || '0';
                setState(prev => ({
                  ...prev,
                  useCustomType: false,
                  customTypeFields: { regex: '', errorMessage: '' }
                }));
              }
            } else if (key === 'function_details' && typeof value === 'string') {
              loadedProps[key] = JSON.parse(value) || {};
            } else {
              loadedProps[key] = value;
            }
          } catch (err) {
            console.error(`Failed to parse ${key}:`, err);
            loadedProps[key] = key === 'function_details' ? {} : value;
          }
        }
      }
    });

    setState(prev => ({
      ...prev,
      properties: loadedProps,
      selectedFunction: loadedProps.function_details?.id?.toString() || '',
      inputJsonFields: loadedProps.function_details?.input_json || {}
    }));

    validateName(loadedProps.name);
  }, [element?.businessObject, validateName]);

  // Event handlers
  const handlePropertyChange = useCallback((key, value) => {
    setState(prev => {
      const updatedProperties = { ...prev.properties, [key]: value };
      const newState = { ...prev, properties: updatedProperties };

      if (modeler && element) {
        const modeling = modeler.get('modeling');
        const update = key === 'name' 
          ? { [key]: value } 
          : { [`node:${key}`]: key === 'function_details' || (key === 'data_type' && prev.useCustomType) 
              ? JSON.stringify(value) 
              : value };

        if (key === 'name') validateName(value);
        modeling.updateProperties(element, update);
      }

      return newState;
    });
  }, [modeler, element, validateName]);

  const handleCustomTypeToggle = useCallback(() => {
    setState(prev => {
      const newUseCustomType = !prev.useCustomType;
      const newProperties = { ...prev.properties };

      if (!newUseCustomType) {
        // Reset to default dropdown value when disabling custom type
        newProperties.data_type = '0';
        if (modeler && element) {
          const modeling = modeler.get('modeling');
          modeling.updateProperties(element, { 'node:data_type': '0' });
        }
      }

      return {
        ...prev,
        useCustomType: newUseCustomType,
        customTypeFields: newUseCustomType ? prev.customTypeFields : { regex: '', errorMessage: '' },
        properties: newProperties
      };
    });
  }, [modeler, element]);

  const handleCustomTypeFieldChange = useCallback((field, value) => {
    setState(prev => {
      const updatedFields = { ...prev.customTypeFields, [field]: value };
      const newState = {
        ...prev,
        customTypeFields: updatedFields
      };

      if (prev.useCustomType) {
        handlePropertyChange('data_type', {
          regex: updatedFields.regex,
          error_message: updatedFields.errorMessage
        });
      }

      return newState;
    });
  }, [handlePropertyChange]);

  const handleFunctionSelect = useCallback((e) => {
    const funcId = e.target.value;
    const selectedFunc = state.availableFunctions.find(f => f.id.toString() === funcId);

    const newInputJsonFields = selectedFunc 
      ? Object.keys(selectedFunc.inputs_expected || {}).reduce((acc, key) => ({
          ...acc,
          [key]: state.inputJsonFields[key] || ''
        }), {})
      : {};

    setState(prev => ({
      ...prev,
      selectedFunction: funcId,
      inputJsonFields: newInputJsonFields,
      error: null
    }));

    handlePropertyChange('function_details', {
      id: funcId ? parseInt(funcId) : null,
      func_name: selectedFunc?.name || '',
      input_json: newInputJsonFields
    });
  }, [state.availableFunctions, state.inputJsonFields, handlePropertyChange]);

  const handleInputJsonChange = useCallback((field, value) => {
    const updatedFields = { ...state.inputJsonFields, [field]: value };
    
    setState(prev => ({
      ...prev,
      inputJsonFields: updatedFields
    }));

    handlePropertyChange('function_details', {
      id: selectedFunction ? parseInt(selectedFunction) : null,
      func_name: state.availableFunctions.find(f => f.id.toString() === selectedFunction)?.name || '',
      input_json: updatedFields
    });
  }, [selectedFunction, state.availableFunctions, state.inputJsonFields, handlePropertyChange]);

  // Render functions
  const renderDropdownField = useCallback((key, label, options) => {
    if (loading) return <div className="mb-3">Loading {label} options...</div>;
    if (error && Object.keys(options).length === 0) {
      return <div className="mb-3 text-red-500">Error loading {label} options</div>;
    }

    return (
      <div key={key} className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium capitalize">{label}</label>
          {key === 'data_type' && (
            <button
              onClick={handleCustomTypeToggle}
              className={`text-sm px-2 py-1 rounded ${
                useCustomType ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {useCustomType ? 'Use Dropdown' : 'Use Custom Type'}
            </button>
          )}
        </div>
        {key === 'data_type' && useCustomType ? (
          <div className="space-y-2">
            <input
              type="text"
              value={customTypeFields.regex}
              onChange={(e) => handleCustomTypeFieldChange('regex', e.target.value)}
              placeholder="Enter Custom Regex"
              className="w-full border rounded p-2 text-sm"
            />
            <input
              type="text"
              value={customTypeFields.errorMessage}
              onChange={(e) => handleCustomTypeFieldChange('errorMessage', e.target.value)}
              placeholder="Enter Message on Error"
              className="w-full border rounded p-2 text-sm"
            />
          </div>
        ) : (
          <select
            value={properties[key] || '0'}
            onChange={(e) => handlePropertyChange(key, e.target.value)}
            className="w-full border rounded p-2 text-sm"
            disabled={key === 'data_type' && useCustomType}
          >
            <option value="0">Select {label}</option>
            {Object.entries(options).map(([value, label]) => (
              <option key={value} value={value}>{value}: {label}</option>
            ))}
          </select>
        )}
      </div>
    );
  }, [loading, error, properties, useCustomType, customTypeFields, handlePropertyChange, handleCustomTypeToggle, handleCustomTypeFieldChange]);

  const renderPropertyField = useCallback((key, type = 'text') => {
    const value = properties[key] ?? DEFAULT_PROPS[key];

    switch (type) {
      case 'boolean':
        return (
          <div key={key} className="flex items-center mb-2">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handlePropertyChange(key, e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded"
            />
            <label className="ml-2 text-sm capitalize">{key.replace(/_/g, ' ')}</label>
          </div>
        );

      case 'textarea':
        return (
          <div key={key} className="mb-3">
            <label className="block text-sm font-medium mb-1 capitalize">
              {key.replace(/_/g, ' ')}
            </label>
            <textarea
              value={value || ''}
              onChange={(e) => handlePropertyChange(key, e.target.value)}
              className="w-full border rounded p-2 text-sm"
              rows={3}
            />
          </div>
        );

      default:
        return (
          <div key={key} className="mb-3">
            <label className="block text-sm font-medium mb-1 capitalize">
              {key.replace(/_/g, ' ')}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handlePropertyChange(key, e.target.value)}
              className={`w-full border rounded p-2 text-sm ${key === 'name' && nameError ? 'border-red-500' : ''}`}
            />
            {key === 'name' && nameError && (
              <div className="text-red-500 text-xs mt-1">{nameError}</div>
            )}
          </div>
        );
    }
  }, [properties, nameError, handlePropertyChange]);

  const renderFunctionDetailsSubform = useCallback(() => {
    if (!properties.is_action_trigger) return null;

    return (
      <div className="mt-4 p-4 border rounded bg-gray-50">
        <h4 className="text-sm font-semibold mb-2">Function Details</h4>
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Choose Function</label>
          <select
            value={selectedFunction}
            onChange={handleFunctionSelect}
            className="w-full border rounded p-2 text-sm"
          >
            <option value="">Select a function</option>
            {availableFunctions.map(func => (
              <option key={func.id} value={func.id}>
                {func.id}: {func.name}
              </option>
            ))}
          </select>
        </div>
        {Object.keys(inputJsonFields).length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            {Object.keys(inputJsonFields).map(field => (
              <div key={field} className="mb-3">
                <label className="block text-sm font-medium mb-1 capitalize">
                  {field.replace(/_/g, ' ')}
                </label>
                <select
                  value={inputJsonFields[field] || ''}
                  onChange={(e) => handleInputJsonChange(field, e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                >
                  <option value="">Select task</option>
                  {taskOptions.map(task => (
                    <option key={task.id} value={task.id}>{task.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }, [
    properties.is_action_trigger,
    selectedFunction,
    availableFunctions,
    inputJsonFields,
    taskOptions,
    handleFunctionSelect,
    handleInputJsonChange
  ]);

  if (loading) return <div className="p-4">Loading properties...</div>;
  if (error && Object.values(dropdownOptions).every(opt => Object.keys(opt).length === 0)) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  return (
    <div className="p-4">
      <h3 className="text-lg font-semibold mb-4 border-b pb-2">
        {element?.type?.replace('bpmn:', '')} Properties
      </h3>

      {error && <div className="text-red-500 mb-4">{error}</div>}

      <div className="space-y-4">
        {renderPropertyField('name')}
        {renderDropdownField('data_type', 'Data Type', dropdownOptions.data_types)}
        {renderDropdownField('que_topic', 'Topic', dropdownOptions.topics)}
        {renderDropdownField('bot_act_as', 'Bot Acts As', dropdownOptions.bot_acts_as)}
        {renderDropdownField('que_category', 'Node Category', dropdownOptions.node_category)}

        <div className="grid grid-cols-2 gap-4">
          {renderPropertyField('is_action_trigger', 'boolean')}
          {renderPropertyField('expects_yes_no', 'boolean')}
          {renderPropertyField('is_ussd_prompt', 'boolean')}
          {renderPropertyField('stems_logic_in_num', 'boolean')}
        </div>

        {renderFunctionDetailsSubform()}

        {renderPropertyField('ans_desc', 'textarea')}
        {renderPropertyField('node_key_words', 'textarea')}
      </div>
    </div>
  );
};

export default PropertiesPanel;